import styled from 'styled-components'
import Skeleton from '~/components/Skeleton'

const OPEN_BADGE_COLORS = {
  OPEN: `
    color: #24B364;
    opacity: 1;
    background: rgba(40, 199, 111, 0.08);
    border: 1px solid #7EDDA9;
  `,
  CLOSED_PERMANENTLY: `
    color: #E64449;
    opacity: 1;
    background: rgba(255, 76, 81, 0.08);
    border: 1px solid #FF9497;
  `,
  CLOSED_TEMPORARILY: `
    color: #E68F3C;
    opacity: 1;
    background: rgba(255, 159, 67, 0.08);
    border: 1px solid #FFC58E;
  `,
  default: `
    color: #2F2B3D;
    opacity: 0.9;
    background: rgba(248, 247, 250, 0.08);
    border: 1px solid rgba(47, 43, 61, 0.08);
  `,
}

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
`

const CardContent = styled.div`
  display: flex;
  padding: 8px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px 8px 0px 0px;
  border: 1px solid #e8e7eb;
  background: var(--white, #fff);
  flex: 1;
`
const Banner = styled.img`
  width: 100%;
  height: 126px;
  align-self: stretch;
  border-radius: 8px;
  object-fit: cover;
  background-color: #6236ff;
`
const LoadingBanner = styled(Skeleton)`
  width: 100%;
  height: 126px;
  align-self: stretch;
  border-radius: 8px;
`

const ProfileInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  margin-top: -48px;
  & > custom-content {
    display: flex;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    align-self: stretch;

    & tags-and-progress {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 32px;
      align-self: stretch;
      flex: 1 0 0;
      & tags {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: flex;
        justify-content: start;
        align-items: flex-start;
        gap: 3px;
        flex: 1 0 0;
      }
    }

    & title-and-address {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      title {
        color: var(--Color-Gray-gray-900, #433c50);
        font-feature-settings: 'liga' off, 'clig' off;
        /* Basic Typography/h6 */
        font-size: 15px;
        font-style: normal;
        font-weight: 500;
        line-height: 22px;
        align-self: stretch;
      }

      & > div {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        align-self: stretch;

        address {
          color: #2f2b3d;
          opacity: 0.7;
          font-feature-settings: 'liga' off, 'clig' off;
          font-size: 13px;
          font-style: normal;
          font-weight: 400;
          line-height: 20px;
          align-self: stretch;
        }
      }
    }
  }
`

const Badge = styled.div(
  ({ color = 'default' }) => `
  display: flex;
  padding: 6px 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  flex: 1 0 0;

  ${OPEN_BADGE_COLORS[color] || OPEN_BADGE_COLORS.default}

  span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    flex: 1 0 0;
    text-align: center;
  }
`
)

const LoadingBadge = styled(Skeleton)`
  display: flex;
  padding: 6px 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 13px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  flex: 1 0 0;
`

const ProfileImage = styled.img`
  width: 88px;
  height: 88px;
  border-radius: 8px;
  object-fit: cover;
`

const LoadingProfileImage = styled(Skeleton)`
  width: 88px;
  height: 88px;
  border-radius: 8px;
`

const CardFooter = styled.div`
  & button {
    background-color: transparent;
    border: none;
    padding: 0;
    &:focus-visible {
      outline: none;
      border: none;
    }

    &:focus {
      outline: none;
      border: none;
    }
  }
  display: flex;
  align-items: center;
  align-self: stretch;
  border-radius: 0px 0px 8px 8px;
  border-right: 1px solid #e8e7eb;
  border-bottom: 1px solid #e8e7eb;
  border-left: 1px solid #e8e7eb;
  background: var(--white, #fff);

  left-content {
    a,
    span {
      padding: 0.3rem;
      aspect-ratio: 1/1;
    }

    a:hover,
    span:hover {
      border-radius: 99999px;
      background-color: #f9f9f9;
    }

    a[id^='google-link-'] {
      margin-top: -2px;
    }

    display: flex;
    height: 48px;
    padding: 8px;
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    border-right: 1px solid #e8e7eb;
  }

  .right-content {
    transition: all 0.2s ease-in-out;
    &:hover {
      background-color: #f0f0f0;
    }

    &:active {
      background-color: #e0e0e0;
    }
    display: flex;
    padding: 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
    span {
      color: #6054f1;

      leading-trim: both;

      text-edge: cap;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 1px;
    }
  }
`

const LoadingCardFooter = styled(Skeleton)`
  & button {
    background-color: transparent;
    border: none;
    padding: 0;
    &:focus-visible {
      outline: none;
      border: none;
    }

    &:focus {
      outline: none;
      border: none;
    }
  }
  display: flex;
  align-items: center;
  align-self: stretch;
  border-radius: 0px 0px 8px 8px;
  border-right: 1px solid #e8e7eb;
  border-bottom: 1px solid #e8e7eb;
  border-left: 1px solid #e8e7eb;
  background: var(--white, #fff);

  left-content {
    a {
      padding: 0.3rem;
      aspect-ratio: 1/1;
    }

    a:hover {
      border-radius: 99999px;
      background-color: #f9f9f9;
    }

    a[id^='google-link-'] {
      margin-top: -2px;
    }

    display: flex;
    height: 48px;
    padding: 8px;
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    border-right: 1px solid #e8e7eb;
  }

  .right-content {
    transition: all 0.2s ease-in-out;
    &:hover {
      background-color: #f0f0f0;
    }

    &:active {
      background-color: #e0e0e0;
    }
    display: flex;
    padding: 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;
    span {
      color: #6054f1;

      leading-trim: both;

      text-edge: cap;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 1px;
    }
  }
`

const Styles = {
  CardFooter,
  Banner,
  Card,
  CardContent,
  ProfileImage,
  ProfileInfoWrapper,
  Badge,
  LoadingBadge,
  LoadingProfileImage,
  LoadingCardFooter,
  LoadingBanner,
}

export default Styles
